import { useEffect, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, Users, Clock, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="border-black/5">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
          <Icon className="h-3.5 w-3.5" />{label}
        </div>
        <div className="font-serif text-3xl mt-1">{value?.toLocaleString?.() ?? value}</div>
      </CardContent>
    </Card>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);

  const load = async (silent = false) => {
    try {
      const r = await api.get(`/admin/analytics?days=${days}`);
      setData(r.data);
    } catch (e) { if (!silent) toast.error(getApiError(e)); }
  };

  useEffect(() => { load(); }, [days]);

  if (!data) {
    return <p className="text-sm text-[color:var(--text-secondary)] mt-4" data-testid="admin-analytics-loading">Loading traffic…</p>;
  }

  const t = data.totals || {};

  return (
    <div className="mt-4 space-y-6" data-testid="admin-analytics-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={Eye} label="All-time" value={t.all_time} />
        <Stat icon={Eye} label={`Visits ${days}d`} value={t.window_visits} />
        <Stat icon={Users} label={`Uniques ${days}d`} value={t.window_uniques} />
        <Stat icon={Clock} label="Visits 24h" value={t.visits_24h} />
        <Stat icon={Users} label="Uniques 24h" value={t.unique_24h} />
        <Stat icon={BarChart3} label="Visits 7d" value={t.visits_7d} />
      </div>

      <Card className="border-black/5">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-serif text-xl">Traffic — last {days} days</h3>
            <div className="flex gap-1" data-testid="admin-analytics-range">
              {[7, 14, 30].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={days === d ? "default" : "outline"}
                  className={days === d ? "bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" : "border-black/10"}
                  onClick={() => setDays(d)}
                  data-testid={`admin-analytics-range-${d}`}
                >
                  {d}d
                </Button>
              ))}
            </div>
          </div>
          <div className="h-64 mt-4" data-testid="admin-analytics-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={(d) => d.slice(5)}
                  tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid rgba(0,0,0,0.1)", fontSize: 12, borderRadius: 6 }}
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                />
                <Bar dataKey="visits" name="Visits" fill="var(--fire-red)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="uniques" name="Unique visitors" fill="var(--bright-green)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-black/5">
          <CardContent className="p-0 overflow-x-auto">
            <div className="p-4 border-b border-black/5 font-medium">Top pages</div>
            <table className="w-full text-sm" data-testid="admin-analytics-top-pages">
              <thead className="bg-[color:var(--bg-secondary)] text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                <tr><th className="p-3 text-left">Path</th><th className="p-3 text-right">Visits</th><th className="p-3 text-right">Uniques</th></tr>
              </thead>
              <tbody>
                {data.top_pages.map((p) => (
                  <tr key={p.path} className="border-t border-black/5">
                    <td className="p-3 font-mono truncate max-w-[300px]">{p.path}</td>
                    <td className="p-3 text-right">{p.visits}</td>
                    <td className="p-3 text-right">{p.uniques}</td>
                  </tr>
                ))}
                {data.top_pages.length === 0 && (
                  <tr><td colSpan={3} className="p-4 text-center text-[color:var(--text-secondary)]">No traffic yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-black/5">
          <CardContent className="p-0 overflow-x-auto">
            <div className="p-4 border-b border-black/5 font-medium">Top referrers</div>
            <table className="w-full text-sm" data-testid="admin-analytics-top-referrers">
              <thead className="bg-[color:var(--bg-secondary)] text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                <tr><th className="p-3 text-left">Referrer</th><th className="p-3 text-right">Visits</th></tr>
              </thead>
              <tbody>
                {data.top_referrers.map((p) => (
                  <tr key={p.referrer} className="border-t border-black/5">
                    <td className="p-3 truncate max-w-[300px]"><a href={p.referrer} target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--fire-red)]">{p.referrer}</a></td>
                    <td className="p-3 text-right">{p.visits}</td>
                  </tr>
                ))}
                {data.top_referrers.length === 0 && (
                  <tr><td colSpan={2} className="p-4 text-center text-[color:var(--text-secondary)]">Direct traffic only.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
